import './assets/css/bootstrap.min.css';
import { Suspense, useEffect, useRef, memo, useState } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import GLTFModal from './components/GLTFModal';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

type Sidebar = '' | 'connect' | 'about' | 'services' | 'portfolio';

interface SceneProps {
    setModelsLoaded: (loaded: boolean) => void;
    isLoading: boolean;
    activeSidebar: Sidebar;
    setActiveSidebar: (value: Sidebar) => void;
    skipInitialDeepLinkModelAnimation: boolean;
    useVisibleZoom: boolean;
}

interface ModelConfig {
    scale: number;
    position: {
        x: number;
        y: number;
        z: number;
    };
}

interface FallbackProps {
    setLoaded: () => void;
}

const SceneComponent = (props: SceneProps) => {

    const [falconLoaded, setFalconLoaded] = useState(false);
    const [yodaLoaded, setYodaLoaded] = useState(false);
    const [mandoLoaded, setMandoLoaded] = useState(false);
    const [bb8Loaded, setBb8Loaded] = useState(false);

    const { camera, gl: { domElement } } = useThree();
    const zoomInIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopZoomIn = () => {
        if (!zoomInIntervalRef.current) return;
        clearInterval(zoomInIntervalRef.current);
        zoomInIntervalRef.current = null;
    };

    const zoomIn = () => {
        stopZoomIn();
        const zoomInterval = props.useVisibleZoom ? 16 : 1000;
        zoomInIntervalRef.current = setInterval(() => {
            if (props.useVisibleZoom) {
                if (camera.position.z >= 1000) camera.position.z -= 2.5;
                else if (camera.position.z >= 800) camera.position.z -= 5;
                else if (camera.position.z >= 600) camera.position.z -= 7.5;
                else if (camera.position.z >= 300) camera.position.z -= 4.5;
                else camera.position.z -= 5;
            } else {
                if (camera.position.z >= 1000) camera.position.z -= 0.25;
                else if (camera.position.z >= 800) camera.position.z -= 0.5;
                else if (camera.position.z >= 600) camera.position.z -= 0.75;
                else if (camera.position.z >= 300) camera.position.z -= 0.45;
                else camera.position.z -= 0.5;
            }

            if (camera.position.z < 100) {
                stopZoomIn();
            }
        }, zoomInterval);
    };

    useEffect(() => {
        camera.position.x = 0;
        camera.position.y = 0;

        if (props.skipInitialDeepLinkModelAnimation) {
            stopZoomIn();
            camera.position.z = 100;
            return () => {
                stopZoomIn();
            };
        }

        camera.position.z = 1500;
        zoomIn();

        return () => {
            stopZoomIn();
        };
    }, [camera, props.skipInitialDeepLinkModelAnimation, props.useVisibleZoom]);

    const controls = useRef<OrbitControlsImpl | null>(null);

    useFrame(() => {
        controls.current?.update?.();
    });

    const initialCameraReset = () => {
        const intervalX = setInterval(() => {
            if (camera.position.x < 1 && camera.position.x >= 0) {
                clearInterval(intervalX);
            }
            if (camera.position.x > 1) camera.position.x -= 1;
            else if (camera.position.x <= 0) camera.position.x += 1;
        }, 0.1);
        const intervalY = setInterval(() => {
            if (camera.position.y < 1 && camera.position.y >= 0) {
                clearInterval(intervalY);
            }
            if (camera.position.y > 1) camera.position.y -= 1;
            else if (camera.position.y <= 1) camera.position.y += 1;
        }, 0.1);
        const intervalZ = setInterval(() => {
            if (camera.position.z < 100 && camera.position.z >= 99) {
                clearInterval(intervalZ);
            }
            if (camera.position.z > 100) camera.position.z -= 1;
            else if (camera.position.z <= 100) camera.position.z += 1;
        }, 0.1);
    };

    useEffect(() => {
        if (falconLoaded && bb8Loaded && yodaLoaded && mandoLoaded) {
            props.setModelsLoaded(true)
        }
    }, [falconLoaded, bb8Loaded, yodaLoaded, mandoLoaded,]);
    useEffect(() => {
        if (falconLoaded && bb8Loaded && yodaLoaded && mandoLoaded && !props.isLoading && !props.skipInitialDeepLinkModelAnimation) {
            initialCameraReset();
        }
    }, [falconLoaded, bb8Loaded, yodaLoaded, mandoLoaded, props.isLoading, props.skipInitialDeepLinkModelAnimation]);

    const ratio = window.innerWidth / window.innerHeight;

    const { Yoda, Falcon, R2D2, Mando } = getModalConfig(ratio);



    return (
        <>
            <OrbitControls ref={controls} args={[camera, domElement]} />

            {/* Mando  */}
            <Suspense fallback={<Fallback setLoaded={() => { setMandoLoaded(true); }} />}>
                <GLTFModal path="/gltf/mando_helmet/scene.gltf"
                    rotationSpeed={[0, 0.009, 0]}
                    rotation={[0, 0, 0]}
                    translate={[0, 0, 0]}
                    animatePosition={[38 * ratio, -10 * ratio, 3]}
                    instantActiveState={props.skipInitialDeepLinkModelAnimation}
                    animatePlanet={(props.activeSidebar === "about" ? true : false)}
                    position={[Mando.position.x, Mando.position.y, Mando.position.z]}

                    planetType="about"
                    scale={[Mando.scale, Mando.scale, Mando.scale]}
                    sat={false}
                    goLeft={(props.activeSidebar === "about" || props.activeSidebar === "" ? false : true)}
                    onPointerDown={() => {
                        initialCameraReset();
                        props.setActiveSidebar("about");
                    }} />
            </Suspense>

            {/* Falcon  */}
            <Suspense fallback={<Fallback setLoaded={() => setFalconLoaded(true)} />}>
                <GLTFModal path="/gltf/falcon1/scene.gltf"
                    rotationSpeed={[0, 0.009, 0]}
                    rotation={[0, 0, 0]}
                    translate={[0, 0, 0]}
                    // animatePosition={[2.3 * (window.innerWidth / window.innerHeight), 0, 3]}
                    animatePosition={[33 * ratio, -3 * ratio, 3]}
                    instantActiveState={props.skipInitialDeepLinkModelAnimation}
                    animatePlanet={(props.activeSidebar === "portfolio" ? true : false)}
                    position={[Falcon.position.x, Falcon.position.y, Falcon.position.z]}
                    planetType="portfolio"
                    scale={[Falcon.scale, Falcon.scale, Falcon.scale]}
                    sat={false}
                    goLeft={(props.activeSidebar === "portfolio" || props.activeSidebar === "" ? false : true)}
                    onPointerDown={() => {
                        initialCameraReset();
                        props.setActiveSidebar("portfolio");

                    }} />
            </Suspense>

            {/* bb8  */}
            <Suspense fallback={<Fallback setLoaded={() => setBb8Loaded(true)} />}>
                <GLTFModal path="/gltf/r2d2/scene.gltf"
                    rotationSpeed={[0, 0.009, 0]}
                    rotation={[0, 0, 0]}
                    translate={[0, 0, 0]}
                    animatePosition={[40 * ratio, -15 * ratio, 0]}
                    instantActiveState={props.skipInitialDeepLinkModelAnimation}
                    animatePlanet={(props.activeSidebar === "connect" ? true : false)}
                    position={[R2D2.position.x, R2D2.position.y, R2D2.position.z]}
                    planetType="connect"
                    scale={[R2D2.scale, R2D2.scale, R2D2.scale]}
                    sat={false}
                    goLeft={(props.activeSidebar === "connect" || props.activeSidebar === "" ? false : true)}
                    onPointerDown={() => {
                        initialCameraReset();
                        props.setActiveSidebar("connect");
                    }} />
            </Suspense>

            {/* yoda  */}
            <Suspense fallback={<Fallback setLoaded={() => setYodaLoaded(true)} />}>
                <GLTFModal path="/gltf/yoda/scene.gltf"
                    rotationSpeed={[0, 0.009, 0]}
                    rotation={[0, 0, 0]}
                    translate={[0, 0, 0]}
                    animatePosition={[(40 * ratio), 0, 3]}
                    instantActiveState={props.skipInitialDeepLinkModelAnimation}
                    animatePlanet={(props.activeSidebar === "services" ? true : false)}
                    position={[Yoda.position.x, Yoda.position.y, Yoda.position.z]}
                    planetType="services"
                    scale={[Yoda.scale, Yoda.scale, Yoda.scale]}
                    sat={false}
                    goLeft={(props.activeSidebar === "services" || props.activeSidebar === "" ? false : true)}
                    onPointerDown={() => {
                        props.setActiveSidebar("services");
                        initialCameraReset();
                    }} />
            </Suspense>
        </>
    );
};

SceneComponent.displayName = "Scene";
export const Scene = memo(SceneComponent);


const getModalConfig = (ratio: number): { Yoda: ModelConfig; Falcon: ModelConfig; R2D2: ModelConfig; Mando: ModelConfig } => {
    let Yoda: ModelConfig;
    let Falcon: ModelConfig;
    let R2D2: ModelConfig;
    let Mando: ModelConfig;
    if (window.innerWidth <= 500) {
        Yoda = {
            scale: 4,
            position: {
                x: 0,
                y: -8,
                z: 0
            }
        }

        Mando = {
            scale: 0.075,
            position: {
                x: -15,
                y: 25,
                z: 0
            }
        }

        Falcon = {
            scale: 0.02,
            position: {
                x: 15,
                y: 13,
                z: 0
            }
        }

        R2D2 = {
            scale: 14,
            position: {
                x: -15,
                y: -40,
                z: 0
            }
        }

    } else if (window.innerWidth < 1100) {
        Yoda = {
            scale: 5,
            position: {
                x: 0,
                y: 0,
                z: 0
            }
        }

        Mando = {
            scale: 0.075,
            position: {
                x: -18.75,
                y: 25,
                z: 0
            }
        }

        Falcon = {
            scale: 0.0375,
            position: {
                x: 25,
                y: 20,
                z: 0
            }
        }

        R2D2 = {
            scale: 10,
            position: {
                x: -37.5,
                y: -10,
                z: 0
            }
        }
    } else {
        Yoda = {
            scale: 7,
            position: {
                x: 0,
                y: 0,
                z: 0
            }
        }

        Mando = {
            scale: 0.1,
            position: {
                x: -25,
                y: 25,
                z: 0
            }
        }

        Falcon = {
            scale: 0.05,
            position: {
                x: 50,
                y: 0,
                z: 0
            }
        }

        R2D2 = {
            scale: 20,
            position: {
                x: -50,
                y: -10,
                z: 0
            }
        }

    }
    return { Yoda, Falcon, R2D2, Mando };
};

const Fallback = ({ setLoaded }: FallbackProps) => {
    useEffect(() => {
        return () => {
            setLoaded();
        };
    }, [setLoaded]);
    return null;
};
